import type { BaseItemSchema, SkillTypes } from "@memorang/types";
import type { ImageType } from "@memorang/ui/components/ImagesCarousel";
import { useMemo } from "react";
import { normalizeBlankStem } from "../helpers/blank-tokens";
import {
	extractAudioUri,
	extractImages,
	extractItemAudioUri,
	extractItemImages,
} from "../helpers/content";
import { getItemInstruction } from "../helpers/session";
import useBlankFormState from "./useBlankFormState";
import useChoiceInteraction from "./useChoiceInteraction";

type UseToeicItemDataOptions = {
	renderItemMediaInLeftPanel?: boolean;
	includeStimulusMediaInline?: boolean;
};

const getLeftImages = ({
	content,
	item,
	renderItemMediaInLeftPanel,
}: {
	content: BaseItemSchema["content"];
	item: BaseItemSchema;
	renderItemMediaInLeftPanel: boolean;
}): ImageType[] => {
	const stimulusImages = extractImages(content);

	if (renderItemMediaInLeftPanel) {
		return stimulusImages.length > 0
			? stimulusImages
			: extractItemImages(content);
	}

	if (stimulusImages.length > 0 || item.recordType === "SCENARIO") {
		return stimulusImages;
	}

	return extractItemImages(content);
};

const getLeftAudioUri = (
	content: BaseItemSchema["content"],
	renderItemMediaInLeftPanel: boolean,
) =>
	renderItemMediaInLeftPanel
		? extractAudioUri(content) || extractItemAudioUri(content)
		: extractAudioUri(content);

const useToeicItemData = (
	item: BaseItemSchema,
	options: UseToeicItemDataOptions = {},
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
) => {
	const { content, response, scoring } = item;
	const {
		renderItemMediaInLeftPanel = false,
		includeStimulusMediaInline = false,
	} = options;

	const skill: SkillTypes = (item.metadata?.skill as SkillTypes) ?? "READING";

	const currentStimulusItemIndex = item.currentStimulusItemIndex;
	const numTotalStimulusItems = item.numTotalStimulusItems;

	const leftImages = getLeftImages({
		content,
		item,
		renderItemMediaInLeftPanel,
	});
	const leftAudioUri = getLeftAudioUri(content, renderItemMediaInLeftPanel);
	const leftPassage = renderItemMediaInLeftPanel
		? ""
		: (content.stimulus?.text ?? "");
	const passageTitle = content.stimulus?.title ?? "";

	const instruction = getItemInstruction(item);
	const questionInstruction = response?.instruction ?? "";
	const stem = content.stem?.text ?? "";

	const ownItemAudioUri = extractItemAudioUri(content);
	const itemAudioUri =
		ownItemAudioUri ||
		(includeStimulusMediaInline ? extractAudioUri(content) : "");
	const ownItemImages: ImageType[] = extractItemImages(content);
	const itemImages =
		includeStimulusMediaInline && ownItemImages.length === 0
			? extractImages(content)
			: ownItemImages;

	const matchingLeft = response?.type === "PAIR_MATCHING" ? response.left : [];
	const matchingRight =
		response?.type === "PAIR_MATCHING" ? response.right : [];
	const matchingLeftLabel =
		response?.type === "PAIR_MATCHING" ? response.leftLabel : undefined;
	const matchingRightLabel =
		response?.type === "PAIR_MATCHING" ? response.rightLabel : undefined;
	const wordBank = response?.type === "TOKEN_MAPPING" ? response.wordBank : [];
	const shuffle =
		response?.type === "OPTION_SELECTION" ? (response.shuffle ?? false) : false;

	const choiceInteraction = useChoiceInteraction(item);
	const {
		isAnswered,
		choices,
		selectedChoiceIds,
		correctAnswerIds,
		handleSelectChoice,
		isMultiSelect,
		numChoicesRequired,
		inReviewMode: choiceInReviewMode,
	} = choiceInteraction;

	const blankForm = useBlankFormState(item);
	const {
		values,
		handleChangeValues,
		inReviewMode: blankInReviewMode,
	} = blankForm;

	const blankStem = useMemo(
		() =>
			normalizeBlankStem(content?.stem?.text || content?.stimulus?.text || ""),
		[content?.stem?.text, content?.stimulus?.text],
	);

	const dropdownFields = useMemo(() => {
		if (
			response?.type !== "INLINE_OPTION_SELECTION" &&
			response?.type !== "BLANK_DROPDOWN"
		) {
			return [];
		}
		return response.blanks.map((b) => ({
			blankId: b.blankId,
			choices: b.choices,
		}));
	}, [response]);

	const dropdownCorrectValues = useMemo(():
		| Record<string, string>
		| undefined => {
		if (!(isAnswered && scoring && "answerKey" in scoring)) {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("blankId" in entry && "choiceId" in entry) {
				map[entry.blankId] = entry.choiceId;
			}
		}
		return map;
	}, [isAnswered, scoring]);

	const dragCorrectWordIdsByBlank = useMemo(():
		| Record<string, string>
		| undefined => {
		if (!(isAnswered && scoring) || scoring.type !== "ANSWER_KEY_MAPPING") {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("blankId" in entry) {
				map[entry.blankId] = "wordId" in entry ? entry.wordId : entry.choiceId;
			}
		}
		return map;
	}, [isAnswered, scoring]);

	const matchingCorrectTokenIdBySlot = useMemo(():
		| Record<string, string>
		| undefined => {
		if (!(isAnswered && scoring) || scoring.type !== "ANSWER_KEY_MAPPING") {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("leftId" in entry) {
				map[entry.leftId] = entry.rightId;
			}
		}
		return map;
	}, [isAnswered, scoring]);

	return {
		skill,
		// Left panel
		leftImages,
		leftAudioUri,
		leftPassage,
		passageTitle,
		instruction,
		// Right panel / stacked
		questionInstruction,
		stem,
		itemAudioUri,
		itemImages,
		// Choice interaction
		isAnswered,
		choices,
		selectedChoiceIds,
		correctAnswerIds,
		handleSelectChoice,
		isMultiSelect,
		numChoicesRequired,
		choiceInReviewMode,
		shuffle,
		// Blank form
		values,
		handleChangeValues,
		blankInReviewMode,
		blankStem,
		// Dropdown
		dropdownFields,
		dropdownCorrectValues,
		// Drag-drop
		wordBank,
		dragCorrectWordIdsByBlank,
		// Matching pair
		matchingLeft,
		matchingRight,
		matchingLeftLabel,
		matchingRightLabel,
		matchingCorrectTokenIdBySlot,
		currentStimulusItemIndex,
		numTotalStimulusItems,
	};
};

export default useToeicItemData;
